import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { AgentAvatar } from './AgentAvatars';
import { Building2, Users, DollarSign, Search, Crown, Shield, UserCheck, X, Edit, Save, Activity, FileText, MessageSquare, ChevronRight, User } from 'lucide-react';

export default function OwnerSubscribers() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [assignModal, setAssignModal] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState('starter');
  const [detailModal, setDetailModal] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [editingCompany, setEditingCompany] = useState(false);
  const [editForm, setEditForm] = useState({});

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try { const r = await api.get('/api/owner/companies'); setCompanies(r.data || []); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleAssignPlan = async () => {
    if (!assignModal) return;
    try { await api.post('/api/owner/subscriptions/assign', { company_id: assignModal.id, plan: selectedPlan }); setAssignModal(null); loadData(); }
    catch (e) { console.error(e); }
  };

  const handleDeleteCompany = async (company) => {
    if (!window.confirm(`Delete "${company.name}" and ALL associated data? This cannot be undone.`)) return;
    try { await api.delete(`/api/owner/companies/${company.id}`); setDetailModal(null); loadData(); }
    catch (e) { console.error(e); alert(e.response?.data?.detail || 'Delete failed'); }
  };

  const openDetail = async (company) => {
    setDetailModal(company); setLoadingDetail(true); setEditingCompany(false);
    try {
      const r = await api.get(`/api/owner/companies/${company.id}/details`);
      setDetailData(r.data);
      setEditForm({ name: r.data.company?.name || '', industry: r.data.company?.industry || '', size: r.data.company?.size || '', description: r.data.company?.description || '' });
    } catch (e) { console.error(e); }
    finally { setLoadingDetail(false); }
  };

  const saveCompanyEdit = async () => {
    try {
      await api.patch(`/api/owner/companies/${detailModal.id}`, editForm);
      setEditingCompany(false); openDetail(detailModal); loadData();
    } catch (e) { console.error(e); }
  };

  const handleChangeUserRole = async (userId, newRole) => {
    try { await api.patch(`/api/owner/users/${userId}/role`, { role: newRole }); openDetail(detailModal); }
    catch (e) { console.error(e); }
  };

  const handleRemoveUser = async (userId) => {
    if (!window.confirm('Remove this user?')) return;
    try { await api.delete(`/api/owner/users/${userId}`); openDetail(detailModal); loadData(); }
    catch (e) { console.error(e); }
  };

  const filtered = companies.filter(c => c.name?.toLowerCase().includes(search.toLowerCase()) || c.industry?.toLowerCase().includes(search.toLowerCase()));

  if (loading) return <div className="loading-screen" style={{ height: 400, background: 'transparent' }}><div className="spinner" style={{ borderTopColor: 'var(--panzix-green)' }} /></div>;

  return (
    <div className="fade-in">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h3 style={{ fontSize: 18, fontWeight: 700 }}>Subscriber Companies ({companies.length})</h3>
          <p style={{ fontSize: 13, color: '#64748b' }}>Click on a company to view full details, users, and manage their account.</p>
        </div>
        <div style={{ position: 'relative' }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: 12, color: '#94a3b8' }} />
          <input data-testid="search-companies" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search companies..."
            style={{ padding: '10px 12px 10px 36px', border: '2px solid #e2e8f0', borderRadius: 10, fontSize: 14, width: 280, outline: 'none' }} />
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="empty-state"><Building2 size={64} /><h4>No companies found</h4></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map(c => (
            <div key={c.id} onClick={() => openDetail(c)} style={{ background: 'white', borderRadius: 14, padding: '16px 20px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', cursor: 'pointer', transition: 'all 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--panzix-green)'} onMouseLeave={e => e.currentTarget.style.borderColor = '#e2e8f0'}
              data-testid={`company-${c.id}`}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg, var(--panzix-green), var(--panzix-purple))', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 18, fontWeight: 800 }}>{c.name?.[0]}</div>
                  <div>
                    <h4 style={{ fontSize: 15, fontWeight: 700 }}>{c.name}</h4>
                    <p style={{ fontSize: 12, color: '#64748b' }}>{c.industry} | {c.size} | {new Date(c.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{ textAlign: 'center' }}><div style={{ fontSize: 18, fontWeight: 700, color: '#2E7D32' }}>{c.user_count}</div><div style={{ fontSize: 10, color: '#94a3b8' }}>Users</div></div>
                  <div style={{ textAlign: 'center' }}><div style={{ fontSize: 18, fontWeight: 700, color: '#7B1FA2' }}>{c.agent_count}</div><div style={{ fontSize: 10, color: '#94a3b8' }}>Agents</div></div>
                  <span className={`agent-status ${c.subscription?.status === 'active' ? 'active' : 'idle'}`} style={{ fontSize: 11 }}>{c.subscription?.plan_name || c.subscription?.plan || 'Free'}</span>
                  <button className="btn-secondary" style={{ padding: '6px 12px', fontSize: 11 }} onClick={e => { e.stopPropagation(); setAssignModal(c); setSelectedPlan(c.subscription?.plan || 'starter'); }}>
                    <DollarSign size={12} style={{ marginRight: 3 }} />Plan
                  </button>
                  <ChevronRight size={16} color="#94a3b8" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Company Detail Modal */}
      {detailModal && (
        <div className="task-modal-overlay" onClick={e => e.target === e.currentTarget && setDetailModal(null)}>
          <div className="task-modal slide-up" style={{ maxWidth: 720, maxHeight: '85vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 48, height: 48, borderRadius: 12, background: 'linear-gradient(135deg, var(--panzix-green), var(--panzix-purple))', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 20, fontWeight: 800 }}>{detailModal.name?.[0]}</div>
                <div>
                  <h3 style={{ margin: 0 }}>{detailModal.name}</h3>
                  <p style={{ fontSize: 12, color: '#64748b' }}>{detailModal.industry}</p>
                </div>
              </div>
              <button onClick={() => setDetailModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={22} /></button>
            </div>

            {loadingDetail ? <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto', borderTopColor: 'var(--panzix-green)' }} /></div> : detailData && (
              <>
                {/* Company Info */}
                <div style={{ background: '#f8fafc', borderRadius: 12, padding: 16, marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <h4 style={{ fontSize: 14, fontWeight: 700 }}>Company Details</h4>
                    <button onClick={() => setEditingCompany(!editingCompany)} className="btn-secondary" style={{ padding: '4px 10px', fontSize: 11 }}>
                      <Edit size={11} style={{ marginRight: 3 }} />{editingCompany ? 'Cancel' : 'Edit'}
                    </button>
                  </div>
                  {editingCompany ? (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      <div className="form-group"><label style={{ fontSize: 11 }}>Name</label><input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} style={{ fontSize: 13, padding: 8 }} /></div>
                      <div className="form-group"><label style={{ fontSize: 11 }}>Industry</label><input value={editForm.industry} onChange={e => setEditForm(f => ({ ...f, industry: e.target.value }))} style={{ fontSize: 13, padding: 8 }} /></div>
                      <div className="form-group"><label style={{ fontSize: 11 }}>Size</label><input value={editForm.size} onChange={e => setEditForm(f => ({ ...f, size: e.target.value }))} style={{ fontSize: 13, padding: 8 }} /></div>
                      <div className="form-group"><label style={{ fontSize: 11 }}>Description</label><input value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} style={{ fontSize: 13, padding: 8 }} /></div>
                      <button className="btn-action" onClick={saveCompanyEdit} style={{ gridColumn: '1/-1', fontSize: 12, padding: 8 }}><Save size={12} style={{ marginRight: 4 }} />Save Changes</button>
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px', fontSize: 13 }}>
                      <div><span style={{ color: '#64748b' }}>Industry:</span> <strong>{detailData.company?.industry}</strong></div>
                      <div><span style={{ color: '#64748b' }}>Size:</span> <strong>{detailData.company?.size}</strong></div>
                      <div><span style={{ color: '#64748b' }}>Created:</span> <strong>{new Date(detailData.company?.created_at).toLocaleDateString()}</strong></div>
                      <div><span style={{ color: '#64748b' }}>Description:</span> <strong>{detailData.company?.description || 'None'}</strong></div>
                    </div>
                  )}
                </div>

                {/* Stats */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 16 }}>
                  {[
                    { label: 'Users', value: detailData.seats?.used || 0, icon: Users, color: '#2E7D32' },
                    { label: 'Agents', value: detailData.agents?.length || 0, icon: Shield, color: '#7B1FA2' },
                    { label: 'Tasks', value: detailData.stats?.tasks || 0, icon: Activity, color: '#1565C0' },
                    { label: 'Messages', value: detailData.stats?.messages || 0, icon: MessageSquare, color: '#F9A825' },
                  ].map(s => (
                    <div key={s.label} style={{ background: '#f8fafc', borderRadius: 10, padding: 12, textAlign: 'center' }}>
                      <s.icon size={16} color={s.color} style={{ marginBottom: 4 }} />
                      <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a' }}>{s.value}</div>
                      <div style={{ fontSize: 10, color: '#94a3b8' }}>{s.label}</div>
                    </div>
                  ))}
                </div>

                {/* Subscription + Seats */}
                {detailData.subscription && (
                  <div style={{ background: '#f0fdf4', borderRadius: 12, padding: 14, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <span style={{ fontWeight: 700, fontSize: 14, color: '#166534' }}>{detailData.subscription.plan_name || detailData.subscription.plan} Plan</span>
                      <span style={{ marginLeft: 10, color: '#64748b', fontSize: 13 }}>${detailData.subscription.price}/mo</span>
                    </div>
                    <div style={{ fontSize: 12, color: '#475569' }}>
                      <strong>{detailData.seats?.used}</strong> / {detailData.seats?.limit} seats used
                    </div>
                  </div>
                )}

                {/* Users */}
                <div style={{ marginBottom: 16 }}>
                  <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Users ({detailData.users?.length || 0})</h4>
                  {(detailData.users || []).length === 0 ? <p style={{ fontSize: 12, color: '#94a3b8' }}>No users</p> : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {detailData.users.map(u => (
                        <div key={u.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: '#f8fafc', borderRadius: 8 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg, var(--panzix-green), var(--panzix-purple))', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 11, fontWeight: 700 }}>{u.name?.[0]}</div>
                            <div><div style={{ fontSize: 13, fontWeight: 600 }}>{u.name}</div><div style={{ fontSize: 11, color: '#94a3b8' }}>{u.email}</div></div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <select value={u.role} onChange={e => handleChangeUserRole(u.id, e.target.value)} style={{ padding: '3px 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 11 }}>
                              <option value="user">user</option><option value="admin">admin</option>
                            </select>
                            <button onClick={() => handleRemoveUser(u.id)} className="btn-delete" style={{ padding: '3px 8px', fontSize: 10 }}><X size={10} /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Deployed Agents */}
                {detailData.agents?.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Deployed AI Employees ({detailData.agents.length})</h4>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {detailData.agents.map(a => (
                        <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                          <AgentAvatar agentType={a.agent_type} size={28} />
                          <div><div style={{ fontSize: 12, fontWeight: 600 }}>{a.agent_name}</div><span className={`agent-status ${a.status}`} style={{ fontSize: 9 }}>{a.status}</span></div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recent Activity */}
                {detailData.activity?.length > 0 && (
                  <div>
                    <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Recent Activity</h4>
                    {detailData.activity.slice(0, 5).map((evt, i) => (
                      <div key={i} style={{ display: 'flex', gap: 8, padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
                        <div style={{ width: 6, height: 6, borderRadius: '50%', marginTop: 5, background: '#3b82f6', flexShrink: 0 }} />
                        <div><p style={{ fontSize: 12, color: '#475569' }}>{evt.message}</p><p style={{ fontSize: 10, color: '#cbd5e1' }}>{new Date(evt.timestamp).toLocaleString()}</p></div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Delete Company */}
                <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #fee2e2' }}>
                  <button className="btn-delete" onClick={() => handleDeleteCompany(detailModal)} style={{ width: '100%', borderRadius: 10, fontSize: 13, padding: '10px' }} data-testid="delete-company">
                    Delete Company & All Data
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Assign Plan Modal */}
      {assignModal && (
        <div className="task-modal-overlay" onClick={e => e.target === e.currentTarget && setAssignModal(null)}>
          <div className="task-modal slide-up">
            <h3>Assign Subscription Plan</h3>
            <p style={{ color: '#64748b', fontSize: 14, marginBottom: 16 }}>Assign a plan to <strong>{assignModal.name}</strong></p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[{id:'starter',name:'Starter',price:'$49/mo',desc:'2 seats'},{id:'professional',name:'Professional',price:'$149/mo',desc:'6 seats'},{id:'enterprise',name:'Enterprise',price:'$399/mo',desc:'11+ seats, SSO'}].map(p => (
                <div key={p.id} style={{ border: `2px solid ${selectedPlan===p.id?'var(--panzix-green)':'#e2e8f0'}`, borderRadius: 10, padding: 14, cursor: 'pointer', background: selectedPlan===p.id?'#f0fdf4':'white' }}
                  onClick={() => setSelectedPlan(p.id)} data-testid={`plan-${p.id}`}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}><div><strong>{p.name}</strong> <span style={{ color: '#64748b', fontSize: 12 }}>{p.desc}</span></div><span style={{ fontWeight: 700, color: 'var(--panzix-green)' }}>{p.price}</span></div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button className="btn-secondary" style={{ flex: 1 }} onClick={() => setAssignModal(null)}>Cancel</button>
              <button className="btn-action" style={{ flex: 1 }} onClick={handleAssignPlan} data-testid="confirm-assign">Assign</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
